import {
  MilestonePanelLayoutDesktop,
  MilestonePanelLayoutMobile,
} from "@/containers/Project/components";
import { SearchParameter } from "@/enums";
import {
  flattenMilestones,
  isMilestoneExpanded,
  shouldBeCollapsedWhenAllSelected,
} from "@/helpers";
import { useIsAdmin } from "@/helpers/permissions";
import { useMobileLayout } from "@/hooks/useAppMedia";
import { useProjectContext } from "@/providers";
import { bool, number, object, string } from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

// maxChildLevel for 4 potential 'layers' of milestone [0, 1, 2, 3] used to calculate offset for cols
const MAX_CHILD_LEVEL = 3;

const MilestonePanel = ({
  milestone,
  childLevel,
  parentId,
  topLevelParent,
  targetDatesRequired,
  skuRequired,
}) => {
  const isMobileLayout = useMobileLayout();
  const {
    milestoneListCollapsed,
    expandedMilestone,
    setExpandedMilestone,
    milestoneListChildren: { topOnly, showOrHideChildren },
    addTopLevelMilestoneToShowOrHideChildren,
    removeTopLevelMilestoneFromShowOrHideChildren,
    canUser,
    project,
    setProject,
  } = useProjectContext();
  const isAdmin = useIsAdmin();
  const [searchParams] = useSearchParams();

  const panelRef = useRef(null);

  // LOCAL STATE
  const [expanded, setExpanded] = useState(!milestoneListCollapsed);
  const [scheduleIndex, setScheduleIndex] = useState(0);
  const [searchParamMilestoneExpanded, setSearchParamMilestoneExpanded] =
    useState(false);

  const milestoneExpanded = isMilestoneExpanded(
    topLevelParent,
    topOnly,
    showOrHideChildren
  );
  const shouldBeCollapsed = shouldBeCollapsedWhenAllSelected(
    topLevelParent,
    topOnly,
    showOrHideChildren
  );

  const milestoneVisible = milestoneExpanded || shouldBeCollapsed;

  useEffect(() => {
    // set whether milestone panel is expanded from the expand/collapse toggle
    let toSetExpanded = !milestoneListCollapsed;

    // only child milestones will have topLevelParent
    if (topLevelParent && milestoneVisible) {
      toSetExpanded = false;
    }

    setExpanded(toSetExpanded);
  }, [milestoneVisible, milestoneListCollapsed, topLevelParent]);

  useEffect(() => {
    if (milestone?.id === expandedMilestone) {
      setExpanded(true);
      panelRef.current.scrollIntoView();
    }
  }, [expandedMilestone, milestone]);

  useEffect(() => {
    if (expanded && milestone.id === expandedMilestone) {
      setExpandedMilestone(null);
    }
  }, [expanded, expandedMilestone, milestone.id, setExpandedMilestone]);

  useEffect(() => {
    const expandTopLevelMilestone = () => {
      const flattenedMilestones = flattenMilestones(milestone.childMilestones);

      if (flattenedMilestones.some(({ id }) => id === milestoneToExpand)) {
        addTopLevelMilestoneToShowOrHideChildren(milestone.id);
      }
    };

    const milestoneToExpand =
      searchParams.get(SearchParameter.Milestone) ||
      searchParams.get(SearchParameter.ExpandMilestone);

    if (!milestoneToExpand || searchParamMilestoneExpanded) return;

    /**
     * If the {@link milestoneToExpand} is nested, the milestone must be visible before it can be expanded.
     * This is achieved through {@link expandTopLevelMilestone} in the top level {@link MilestonePanel}.
     * */
    const topLevelMilestoneIsCollapsed = !!(childLevel && !milestoneVisible);

    if (topLevelMilestoneIsCollapsed) return;

    if (milestoneToExpand === milestone.id) {
      setExpandedMilestone(milestoneToExpand);
    }

    const milestoneToExpandIsNested =
      !childLevel && milestoneToExpand !== milestone.id;

    if (milestoneToExpandIsNested) {
      expandTopLevelMilestone();
    }

    setSearchParamMilestoneExpanded(true);
  }, [
    addTopLevelMilestoneToShowOrHideChildren,
    childLevel,
    milestone.childMilestones,
    milestone.id,
    milestoneVisible,
    searchParamMilestoneExpanded,
    searchParams,
    setExpandedMilestone,
  ]);

  const toggleChildren = () => {
    const target = topLevelParent ?? milestone.id;
    if (!showOrHideChildren.includes(target)) {
      addTopLevelMilestoneToShowOrHideChildren(target);
    } else {
      removeTopLevelMilestoneFromShowOrHideChildren(target);
    }
  };

  if (childLevel > 0 && milestoneVisible) return null;

  return (
    <>
      {isMobileLayout ? (
        <MilestonePanelLayoutMobile
          milestone={milestone}
          childLevel={childLevel}
          maxChildLevel={MAX_CHILD_LEVEL}
          expanded={expanded}
          toggleExpanded={() => {
            setExpanded((expanded) => !expanded);
          }}
          handleScheduleChange={setScheduleIndex}
          scheduleIndex={scheduleIndex}
          parentId={parentId}
          toggleChildren={toggleChildren}
          targetDatesRequired={targetDatesRequired}
          skuRequired={skuRequired}
          canUser={canUser}
          project={project}
          setProject={setProject}
          isAdmin={isAdmin}
        />
      ) : (
        <MilestonePanelLayoutDesktop
          milestone={milestone}
          childLevel={childLevel}
          maxChildLevel={MAX_CHILD_LEVEL}
          expanded={expanded}
          toggleExpanded={() => {
            setExpanded((expanded) => !expanded);
          }}
          handleScheduleChange={setScheduleIndex}
          scheduleIndex={scheduleIndex}
          parentId={parentId}
          toggleChildren={toggleChildren}
          targetDatesRequired={targetDatesRequired}
          skuRequired={skuRequired}
          canUser={canUser}
          project={project}
          setProject={setProject}
          isAdmin={isAdmin}
        />
      )}
      <div ref={panelRef}></div>
    </>
  );
};

MilestonePanel.propTypes = {
  milestone: object,
  childLevel: number,
  parentId: string,
  topLevelParent: string,
  targetDatesRequired: bool,
  skuRequired: bool,
};

export { MilestonePanel };
